import { ArrowUpIcon } from '@heroicons/react/20/solid';
import { ChatBubbleOvalLeftIcon, HeartIcon } from '@heroicons/react/24/outline';
import { LinearProgress } from '@mui/joy';

import { useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  useCreateCommentMutation,
  useHubCommentLikeMutation,
  useHubCommentUnlikeMutation,
  useHubCommentsInfinite,
  useSelectedTenant,
} from '@/hooks/useSwr';
import { CommentSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { Error } from './Hub/Error';
import RelativeTime from './Hub/RelativeTime';
import { PlainTextEditor } from './PlainTextEditor';

const ProfileCircle = ({
  active,
  name,
}: {
  active?: boolean;
  name: string;
}) => {
  return (
    <div
      className={cn(
        'relative flex h-5 w-5 items-center justify-center gap-2 rounded-[40px] bg-gradient-to-br text-center text-xs uppercase text-white',
        active
          ? 'from-brand-green-500 to-cyan-400'
          : 'from-gray-600 to-gray-400'
      )}
    >
      {name.charAt(0)}
    </div>
  );
};

function HubComment({
  owner,
  repo,
  comment,
}: {
  owner: string;
  repo: string;
  comment: CommentSchema;
}) {
  const [expandThread, setExpandThreadState] = useState(false);
  const { trigger: likeComment } = useHubCommentLikeMutation(
    owner,
    repo,
    comment.id
  );
  const { trigger: unlikeComment } = useHubCommentUnlikeMutation(
    owner,
    repo,
    comment.id
  );
  const { mutate } = useHubCommentsInfinite({
    owner,
    repo,
    parentId: comment.parent_id,
  });
  const onLike = async () => {
    if (comment.liked_by_auth_user) {
      await unlikeComment({});
    } else {
      await likeComment({});
    }
    mutate();
  };

  // Comment created at is in UTC, but it isn't returned from the backend with 'Z' at the end so we need to add it
  const commentCreatedAtWithUTCIndicator = comment.created_at.endsWith('Z')
    ? comment.created_at
    : comment.created_at + 'Z';

  const setExpandThread = (s: boolean) => {
    setExpandThreadState(s);
  };
  return (
    <div className="flex flex-1 flex-col items-stretch gap-3 py-3">
      <div className="flex flex-row items-center justify-start gap-1.5">
        <div className="flex-0">
          <ProfileCircle active={false} name={comment.comment_by_name ?? ' '} />
        </div>
        <div className="text-xs font-semibold leading-none text-slate-900 dark:text-slate-100">
          {comment.comment_by_name}
        </div>
        <div className="grow" />
        <div className="text-xs font-normal leading-none text-slate-500 dark:text-slate-400">
          <RelativeTime
            utcTime={commentCreatedAtWithUTCIndicator}
            shortFormat
          />
        </div>
      </div>
      <div className="flex flex-row items-stretch justify-stretch">
        {comment.parent_id && (
          <div className="flex-0 mr-1.5 flex w-5 flex-col items-center justify-stretch">
            <div className="h-full w-px bg-tertiary" />
          </div>
        )}
        <div className="flex flex-1 flex-col items-stretch gap-3">
          <div className="whitespace-pre-line text-xs font-normal leading-none text-slate-700 dark:text-slate-200">
            {comment.content}
          </div>
          <div className="flex flex-row items-baseline justify-start gap-1.5">
            {!comment.parent_id && (
              <>
                <button
                  type="button"
                  className="text-xs font-normal leading-none text-slate-500 hover:underline dark:text-slate-400"
                  onClick={() => setExpandThread(!expandThread)}
                >
                  {comment.num_sub_comments}{' '}
                  {comment.num_sub_comments !== 1 ? 'replies' : 'reply'}
                </button>
                <div className="text-xs font-normal leading-none text-slate-500 dark:text-slate-400">
                  •
                </div>
              </>
            )}
            <div className="text-xs font-normal leading-none text-slate-500 dark:text-slate-400">
              {comment.num_likes} like{comment.num_likes !== 1 && 's'}
            </div>
            <div className="grow" />
            {!comment.parent_id && (
              <button type="button" onClick={() => setExpandThread(true)}>
                <ChatBubbleOvalLeftIcon
                  className={cn('hover:fill-secondary h-4 w-4')}
                />
              </button>
            )}
            <button type="button" onClick={onLike}>
              <HeartIcon
                className={cn(
                  'h-4 w-4',
                  comment.liked_by_auth_user
                    ? 'fill-ls-black hover:fill-gray-700 dark:hover:fill-gray-200'
                    : 'hover:fill-gray-200 dark:hover:fill-gray-700'
                )}
              />
            </button>
          </div>
        </div>
      </div>
      {expandThread && <InnerHubComments parentId={comment.id} />}
    </div>
  );
}

function CommentBox({
  owner,
  repo,
  parentId,
}: {
  owner: string;
  repo: string;
  parentId: string | null;
}) {
  const [newComment, setNewComment] = useState('');
  const { trigger, isMutating, error, reset } = useCreateCommentMutation({
    owner,
    repo,
    parentId,
  });
  const { mutate: mutateComments } = useHubCommentsInfinite({
    owner,
    repo,
    parentId,
  });
  const { mutate: mutateRoot } = useHubCommentsInfinite({
    owner,
    repo,
    parentId: null,
  });
  const onSubmit = async () => {
    if (isMutating || !newComment) return;
    await trigger({ json: { content: newComment } });
    setNewComment('');
    reset();
    mutateComments();
    if (parentId) mutateRoot();
  };
  const errorMessage = error?.message;

  return (
    <div className="flex flex-col rounded-lg bg-gray-50 px-4 py-2.5 dark:bg-gray-900">
      <div className="flex flex-row items-center gap-2 ">
        <div className="to-brand-400 flex h-5 w-5 items-center justify-center gap-2 rounded-[40px] bg-gradient-to-br from-brand-green-500">
          <ProfileCircle active={true} name={''} />
        </div>
        <PlainTextEditor
          value={newComment}
          onChange={(v) => setNewComment(v)}
          placeholder="Add your comment..."
          readOnly={isMutating}
          onKeyDown={(event) => {
            if (
              (event.key === 'Enter' || event.key === 'NumpadEnter') &&
              (event.metaKey || event.ctrlKey)
            ) {
              onSubmit();
            }
          }}
        />
        {newComment && (
          <button type="button" onClick={onSubmit} disabled={isMutating}>
            <ArrowUpIcon
              className={cn(
                'h-4 w-4 rounded-full bg-brand-green-400 text-white',
                isMutating ? 'animate-pulse opacity-75' : 'hover:ring'
              )}
            />
          </button>
        )}
      </div>
      {errorMessage && (
        <div className="pb-1 pt-2 text-xs text-red-500 dark:text-red-400">
          Error: {errorMessage}
        </div>
      )}
    </div>
  );
}
function InnerHubComments({ parentId = null }: { parentId?: string | null }) {
  const { owner, repo } = useParams<{ owner: string; repo: string }>();
  const {
    data: commentPages,
    isLoading,
    error,
    size,
    setSize,
  } = useHubCommentsInfinite({ owner: owner, repo: repo!, parentId });
  const { data: selectedTenant } = useSelectedTenant();

  if (error) return <Error>{error.message}</Error>;
  if (!commentPages && isLoading) return <LinearProgress />;
  if (!commentPages) {
    // should never happen
    return <div>No comments - make a first one!</div>;
  }

  const visibleComments = commentPages.map((page) => page.comments).flat();

  const totalComments = commentPages[0].total;
  const remainingComments = totalComments - visibleComments.length;

  return (
    <div className="flex flex-col items-start justify-start gap-9 pl-4">
      <div className="flex flex-col items-stretch justify-start gap-3 self-stretch">
        {!parentId && (
          <div className="flex flex-col items-start justify-start">
            <div className="text-base font-medium leading-snug text-slate-900 dark:text-slate-100">
              Comments ({totalComments})
            </div>
          </div>
        )}
        <div
          className={cn(
            'flex flex-col items-stretch justify-start overflow-y-auto',
            !parentId && 'divide-y divide-secondary'
          )}
        >
          {visibleComments.map((c, i) => (
            <HubComment key={i} owner={owner!} repo={repo!} comment={c} />
          ))}
        </div>

        {totalComments > visibleComments.length && (
          <div
            onClick={() => setSize(size + 1)}
            className="cursor-pointer text-xs font-semibold leading-none text-brand-green-500"
          >
            See {Math.min(20, remainingComments)} more comments
          </div>
        )}
        {selectedTenant && (
          <CommentBox owner={owner!} repo={repo!} parentId={parentId} />
        )}
      </div>
    </div>
  );
}

export function HubComments() {
  return (
    <div className="py-6 pr-4">
      <InnerHubComments />
    </div>
  );
}
